import { differenceInCalendarDays } from "date-fns";
import { User } from "features/user/types";

type UserWithSubscription = {
  subscription?: {
    subscription?: {
      status?: string;
      trial_end?: number;
    };
  };
};

/**
 * Pure (non-React) helpers for detecting reverse trial state.
 *
 * A "reverse trial" is the new full-featured, no-credit-card-required trial
 * model. Per product direction, we treat every user whose subscription status
 * is "in_trial" as a reverse trial user — the backend is migrating all new
 * trial signups to this model, and legacy paid customers are never in this
 * state anyway.
 *
 * During a reverse trial:
 *  - all features are unlocked via time-limited entitlements (server-granted)
 *  - no payment method is required
 *  - upsell/plan-quote modals are hidden
 *  - limit displays show "Unlimited during trial"
 *  - at trial end, the user is routed to /onboarding/manage-plan to pick a plan
 */
/**
 * Pure check on the subscription status string. Exported so subapps that
 * read from a different store (e.g. @insights) can reuse the rule without
 * importing the User-typed `isReverseTrial`.
 */
export const isReverseTrialStatus = (
  status: string | null | undefined
): boolean => status === "in_trial";

export const isReverseTrial = (
  user: UserWithSubscription | null | undefined
): boolean => {
  return isReverseTrialStatus(user?.subscription?.subscription?.status);
};

/**
 * Broader "is this user in any trial-like state" check. SKE-3484 follow-up:
 * the canonical Chargebee trial status is "in_trial", but some legacy /
 * mid-migration data has surfaced trial-period users with statuses like
 * "trialing" (legacy) or "cancelled" within their trial window. Use this
 * helper for *bypass* gates (don't lock evaluating users out of headline
 * actions) — keep `isReverseTrial` for the canonical check that drives
 * banner copy + entitlement assumptions.
 */
export const isInAnyTrialState = (user: User | null | undefined): boolean => {
  const sub = user?.subscription?.subscription;
  if (!sub) return false;
  if (sub.status === "in_trial") return true;
  if ((sub.status as string) === "trialing") return true;
  // Cancelled-during-trial: status flips to "cancelled" but the trial_end
  // is still in the future. Treat as trial for the purposes of the
  // account-add gate so users can still finish evaluating.
  if (
    sub.status === "cancelled" &&
    typeof sub.trial_end === "number" &&
    sub.trial_end * 1000 > Date.now()
  ) {
    return true;
  }
  return false;
};

/**
 * Returns the Date at which the trial ends, or null if the user is not in a
 * trial or the backend hasn't set trial_end yet.
 */
export const getTrialEndDate = (user: User | null | undefined): Date | null => {
  const subscription = user?.subscription?.subscription;
  if (!subscription || subscription.status !== "in_trial") return null;
  // Prefer an explicit positive trial_end; Chargebee occasionally writes 0 to
  // cleared timestamps so falsy-coalescing to next_billing_at could surface a
  // post-trial renewal date. Guard with a positive-number check.
  const trialEnd =
    typeof subscription.trial_end === "number" && subscription.trial_end > 0
      ? subscription.trial_end
      : undefined;
  const nextBilling =
    typeof subscription.next_billing_at === "number" &&
    subscription.next_billing_at > 0
      ? subscription.next_billing_at
      : undefined;
  const endTimestamp = trialEnd ?? nextBilling;
  if (!endTimestamp) return null;
  return new Date(endTimestamp * 1000);
};

/**
 * Whole calendar days remaining in the trial, or null if not in a trial.
 * Matches the semantics used by SubscriptionBanner (differenceInCalendarDays).
 */
export const getDaysLeftInTrial = (
  user: User | null | undefined
): number | null => {
  const endDate = getTrialEndDate(user);
  if (!endDate) return null;
  return differenceInCalendarDays(endDate, new Date());
};

/**
 * Centralised payment-method detection. Previously duplicated in
 * use-subscription-banner-state.ts and payment-method-section.tsx.
 *
 * Two independent signals, either of which indicates a payable method:
 *  1. A non-empty `primary_payment_source_id` — an active source on the
 *     customer record. An empty string counts as "no source" because
 *     Chargebee occasionally surfaces cleared sources as `""` rather than
 *     omitting the field, and a plain `!!` check would mis-classify those
 *     users as having a card on file.
 *  2. `card_status === "valid"` — a safety fallback for the short window
 *     after a card is accepted but before Chargebee has propagated the new
 *     source ID onto the customer. Without this the banner/UI briefly flips
 *     back to "no payment method" even though a valid card exists.
 */
export const getHasPaymentMethod = (user: User | null | undefined): boolean => {
  const customer = user?.subscription?.customer;
  const sourceId = customer?.primary_payment_source_id;
  const hasSource = typeof sourceId === "string" && sourceId.length > 0;
  return hasSource || customer?.card_status === "valid";
};
